import { DISK_CRITICAL_RATIO, DISK_WARNING_RATIO } from '@cp/shared';
import type { NodeView } from '@/types/api';

/**
 * What to show for a node: a node that never had an agent is waiting for one,
 * which is not the same as one that went quiet. A stale heartbeat reads as
 * offline whatever the stored state says: the stored value is what was last
 * decided, the heartbeat is what is true now.
 */
export function nodeState(node: NodeView): string {
  if (node.agentVersion === null) return 'awaiting';
  return node.stale ? 'offline' : node.schedulingState;
}

/**
 * Disk pressure, using the same thresholds the scheduler does.
 *
 * Past the critical line a node stops taking work, so an operator reading the
 * servers page should see the number turn before the scheduler quietly stops
 * using the server and nobody can say why.
 */
export function diskTone(used: number, total: number): string {
  if (total <= 0) return 'tone-down';
  const ratio = used / total;
  if (ratio >= DISK_CRITICAL_RATIO) return 'tone-down';
  return ratio >= DISK_WARNING_RATIO ? 'tone-degraded' : '';
}

/** A node the scheduler places new sites on: accepting sites and reporting. */
export function isSchedulable(node: NodeView): boolean {
  return node.schedulingState === 'active' && !node.stale;
}

/** A node a site can be moved to: schedulable, with an agent, and not the one it is on. */
export function canReceiveSite(node: NodeView, site: { readonly nodeId: string | null }): boolean {
  return node.id !== site.nodeId && isSchedulable(node) && node.agentVersion !== null;
}
